import React, { useState, useEffect, useCallback } from 'react';
import DashboardContainer from '../components/layout/DashboardContainer';
import MetricsCards from '../components/analytics/MetricsCards';
import MessageChart from '../components/analytics/MessageChart';
import UsagePieChart from '../components/analytics/UsagePieChart';
import CampaignTable from '../components/analytics/CampaignTable';
import CustomerInsights from '../components/analytics/CustomerInsights';
import { BarChart3, TrendingUp, PieChart, Megaphone, Users } from 'lucide-react';
import api from '../services/api';
import './AnalyticsPage.css';

const PERIODS = [
    { value: '1d', label: 'Today' },
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
];

export default function AnalyticsPage() {
    const [period, setPeriod] = useState('30d');
    const [summary, setSummary] = useState(null);
    const [trends, setTrends] = useState(null);
    const [campaigns, setCampaigns] = useState(null);
    const [usage, setUsage] = useState(null);
    const [customers, setCustomers] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const [summaryRes, trendsRes, campaignsRes, usageRes, customersRes] = await Promise.allSettled([
                api.get(`/analytics/summary?period=${period}`),
                api.get(`/analytics/trends?period=${period}`),
                api.get(`/analytics/campaigns?period=${period}`),
                api.get(`/analytics/usage?period=${period}`),
                api.get(`/analytics/customers?period=${period}`),
            ]);

            if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
            if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.data);
            if (campaignsRes.status === 'fulfilled') setCampaigns(campaignsRes.value.data);
            if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
            if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data);
        } catch (err) {
            console.error('Failed to fetch analytics:', err);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    return (
        <DashboardContainer>
            <div className="analytics-page">
                {/* Header */}
                <div className="analytics-header">
                    <div>
                        <h1>
                            <BarChart3 size={22} />
                            Analytics
                        </h1>
                        <p>Overview of your messaging activity and performance</p>
                    </div>
                    <div className="period-selector">
                        {PERIODS.map(p => (
                            <button
                                key={p.value}
                                className={`period-btn ${period === p.value ? 'active' : ''}`}
                                onClick={() => setPeriod(p.value)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="analytics-loading">
                        <div className="spinner" />
                        <span>Loading analytics...</span>
                    </div>
                ) : (
                    <>
                        {/* Metric Cards */}
                        <MetricsCards data={summary} />

                        {/* Charts Row */}
                        <div className="analytics-charts">
                            {/* Message Trends */}
                            <div className="chart-card">
                                <div className="chart-card__header">
                                    <div>
                                        <div className="chart-card__title">
                                            <TrendingUp size={16} /> Message Trends
                                        </div>
                                        <div className="chart-card__subtitle">
                                            Inbound vs outbound messages
                                        </div>
                                    </div>
                                </div>
                                <MessageChart data={trends} />
                            </div>

                            {/* Usage Breakdown */}
                            <div className="chart-card">
                                <div className="chart-card__header">
                                    <div>
                                        <div className="chart-card__title">
                                            <PieChart size={16} /> Response Breakdown
                                        </div>
                                        <div className="chart-card__subtitle">
                                            How messages are being handled
                                        </div>
                                    </div>
                                </div>
                                <UsagePieChart data={usage} />
                            </div>
                        </div>

                        {/* Customer Insights */}
                        <CustomerInsights data={customers} />

                        {/* Campaign Stats */}
                        <div className="campaign-table-card">
                            <div className="chart-card__header">
                                <div>
                                    <div className="chart-card__title">
                                        <Megaphone size={16} /> Campaign Performance
                                    </div>
                                    <div className="chart-card__subtitle">
                                        Delivery and engagement rates
                                    </div>
                                </div>
                            </div>
                            <CampaignTable data={campaigns} />
                        </div>
                    </>
                )}
            </div>
        </DashboardContainer>
    );
}
